import { z } from 'zod';
/**
 * Announcement wire shapes for the admin CMS and the driver PWA (the
 * dismissible landing popup). Mirrors the coupons.ts convention: snake_case
 * Mongo fields project to camelCase on the wire; tenant_id / created_by are
 * filled server-side and never accepted from the client.
 */
export const AnnouncementAudienceSchema = z.enum([
    'all_drivers',
    'tenant',
    'fleet',
    'residential_owners',
]);
/**
 * Optional geo-audience LAYER — an extra constraint AND-ed on top of the
 * base audience, resolved at fetch time against the driver's derived
 * geography (home + session location states, and a representative point).
 * Within geo, `states` and `radius` are OR-ed. Not exposed on the driver
 * card (a targeting detail); present only on the admin + upsert shapes.
 */
export const AnnouncementGeoSchema = z.object({
    states: z.array(z.string().min(1).max(60)).max(60).nullable().optional(),
    radius: z.object({
        lat: z.number().min(-90).max(90),
        lng: z.number().min(-180).max(180),
        km: z.number().positive().max(20_000),
    }).nullable().optional(),
});
/** Driver-facing card shape — GET /me/announcements returns at most one. */
export const AnnouncementPublicSchema = z.object({
    id: z.string(),
    title: z.string(),
    body: z.string(),
    imageUrl: z.string().nullable().optional(),
    /** Spotify-canvas-style motion card: muted looped autoplay on the
     *  driver card, imageUrl doubling as poster + reduced-motion/error
     *  fallback. Same external-URL posture as imageUrl. */
    videoUrl: z.string().nullable().optional(),
    ctaLabel: z.string().nullable().optional(),
    ctaLink: z.string().nullable().optional(),
});
/** Admin projection — everything the CMS list + form need. */
export const AnnouncementAdminSchema = AnnouncementPublicSchema.extend({
    audience: AnnouncementAudienceSchema,
    fleetId: z.string().nullable().optional(),
    /** Optional geo-audience layer (see AnnouncementGeoSchema). Null = none. */
    geo: AnnouncementGeoSchema.nullable().optional(),
    priority: z.number().int(),
    startsAt: z.string().nullable().optional(),
    endsAt: z.string().nullable().optional(),
    isActive: z.boolean(),
    createdAt: z.string(),
});
/** Payload the admin form sends on create / update. */
export const AnnouncementUpsertBodySchema = z.object({
    title: z.string().min(1).max(120),
    body: z.string().min(1).max(600),
    imageUrl: z.string().url().max(500).nullable().optional(),
    videoUrl: z.string().url().max(500).nullable().optional(),
    ctaLabel: z.string().max(40).nullable().optional(),
    ctaLink: z.string().max(500).nullable().optional(),
    audience: AnnouncementAudienceSchema.default('tenant'),
    fleetId: z.string().nullable().optional(),
    geo: AnnouncementGeoSchema.nullable().optional(),
    priority: z.number().int().default(0),
    startsAt: z.string().nullable().optional(),
    endsAt: z.string().nullable().optional(),
    isActive: z.boolean().default(true),
});
//# sourceMappingURL=announcements.js.map